import { useEffect, useRef, useState, type RefObject } from "react";

export type ImageFrame = {
  left: number;
  top: number;
  width: number;
  height: number;
};

type FitMode = "contain" | "cover";
type Anchor = "top" | "center";

/**
 * Returns the natural aspect ratio (width / height) of an image source.
 */
export function useImageRatio(src: string): number | null {
  const [ratio, setRatio] = useState<number | null>(null);

  useEffect(() => {
    const image = new Image();
    image.onload = () => {
      if (image.naturalWidth > 0 && image.naturalHeight > 0) {
        setRatio(image.naturalWidth / image.naturalHeight);
      }
    };
    image.src = src;

    return () => {
      image.onload = null;
    };
  }, [src]);

  return ratio;
}

/**
 * Measures the exact rectangle where an image is actually painted inside a
 * container that uses `background-size: contain` / `cover` semantics.
 * This makes pixel positions inside the photo reliable on every screen size.
 */
export function usePaintedImageFrame(
  containerRef: RefObject<HTMLElement | null>,
  naturalRatio: number | null,
  mobileFit: FitMode,
  mobileAnchor: Anchor,
  desktopFit: FitMode,
  desktopAnchor: Anchor,
): ImageFrame | null {
  const [frame, setFrame] = useState<ImageFrame | null>(null);
  const configRef = useRef({ mobileFit, mobileAnchor, desktopFit, desktopAnchor });
  configRef.current = { mobileFit, mobileAnchor, desktopFit, desktopAnchor };

  useEffect(() => {
    const element = containerRef.current;
    if (!element || !naturalRatio) return;

    let frameId = 0;

    const measure = () => {
      frameId = 0;
      const rect = element.getBoundingClientRect();
      const containerWidth = rect.width;
      const containerHeight = rect.height;
      if (containerWidth <= 0 || containerHeight <= 0) return;

      const isDesktop = window.matchMedia("(min-width: 768px)").matches;
      const { mobileFit: mFit, mobileAnchor: mAnchor, desktopFit: dFit, desktopAnchor: dAnchor } =
        configRef.current;
      const fit = isDesktop ? dFit : mFit;
      const anchor = isDesktop ? dAnchor : mAnchor;

      const containerIsWider = containerWidth / containerHeight > naturalRatio;
      let width = 0;
      let height = 0;

      if (fit === "contain") {
        if (containerIsWider) {
          height = containerHeight;
          width = containerHeight * naturalRatio;
        } else {
          width = containerWidth;
          height = containerWidth / naturalRatio;
        }
      } else if (containerIsWider) {
        width = containerWidth;
        height = containerWidth / naturalRatio;
      } else {
        height = containerHeight;
        width = containerHeight * naturalRatio;
      }

      const left = (containerWidth - width) / 2;
      const top = anchor === "top" ? 0 : (containerHeight - height) / 2;

      setFrame((previous) => {
        if (
          previous &&
          Math.abs(previous.left - left) < 0.5 &&
          Math.abs(previous.top - top) < 0.5 &&
          Math.abs(previous.width - width) < 0.5 &&
          Math.abs(previous.height - height) < 0.5
        ) {
          return previous;
        }
        return { left, top, width, height };
      });
    };

    const schedule = () => {
      if (frameId) return;
      frameId = window.requestAnimationFrame(measure);
    };

    schedule();

    const observer = new ResizeObserver(schedule);
    observer.observe(element);
    window.addEventListener("resize", schedule);

    const mediaQuery = window.matchMedia("(min-width: 768px)");
    mediaQuery.addEventListener("change", schedule);

    return () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      observer.disconnect();
      window.removeEventListener("resize", schedule);
      mediaQuery.removeEventListener("change", schedule);
    };
  }, [containerRef, naturalRatio, mobileFit, mobileAnchor, desktopFit, desktopAnchor]);

  return frame;
}